import React from 'react';

// Drawn reference pictures for the upload step. Made-up shapes only: no real card, number or person.
export type SketchKind = 'selfie' | 'pan' | 'aadhaarFront' | 'aadhaarBack';
export type SketchFlaw = 'good' | 'cut' | 'glare' | 'blur' | 'tilt' | 'dark' | 'far';

const INK = '#2f3a36';
const LINE = '#8d9894';
const DESK = '#ece8df';
const CARD = '#ffffff';

const Lines = ({ x, y, widths, gap = 9 }: { x: number; y: number; widths: number[]; gap?: number }) => (
  <>{widths.map((w, i) => <rect key={i} x={x} y={y + i * gap} width={w} height="4" rx="2" fill={LINE} />)}</>
);

const Face = ({ cx, cy, r }: { cx: number; cy: number; r: number }) => (
  <>
    <circle cx={cx} cy={cy} r={r} fill="#d9c7ad" />
    <circle cx={cx - r * 0.34} cy={cy - r * 0.12} r={r * 0.09} fill={INK} />
    <circle cx={cx + r * 0.34} cy={cy - r * 0.12} r={r * 0.09} fill={INK} />
    <path d={`M${cx - r * 0.32} ${cy + r * 0.34} Q${cx} ${cy + r * 0.56} ${cx + r * 0.32} ${cy + r * 0.34}`} stroke={INK} strokeWidth="2" fill="none" strokeLinecap="round" />
  </>
);

const CardBody = ({ kind }: { kind: SketchKind }) => {
  if (kind === 'pan') {
    return (
      <>
        <rect x="14" y="12" width="132" height="7" rx="3" fill={LINE} />
        <Lines x={14} y={30} widths={[62, 80, 44, 56]} />
        <rect x="104" y="28" width="30" height="36" rx="3" fill="#d9c7ad" />
        <circle cx="119" cy="42" r="8" fill="#b9a688" />
        <rect x="14" y="70" width="70" height="8" rx="2" fill={INK} />
      </>
    );
  }
  if (kind === 'aadhaarBack') {
    return (
      <>
        <rect x="14" y="12" width="60" height="7" rx="3" fill={LINE} />
        <Lines x={14} y={28} widths={[120, 132, 104, 84]} />
        <rect x="46" y="70" width="68" height="8" rx="2" fill={INK} />
      </>
    );
  }
  return (
    <>
      <rect x="14" y="12" width="132" height="7" rx="3" fill={LINE} />
      <rect x="14" y="27" width="34" height="38" rx="3" fill="#d9c7ad" />
      <circle cx="31" cy="42" r="9" fill="#b9a688" />
      <Lines x={58} y={30} widths={[76, 58, 66]} />
      <rect x="46" y="70" width="68" height="8" rx="2" fill={INK} />
    </>
  );
};

const CardShot = ({ kind, flaw }: { kind: SketchKind; flaw: SketchFlaw }) => {
  const scale = flaw === 'far' ? 0.5 : 1;
  const pos = flaw === 'cut' ? 'translate(62 26)' : flaw === 'far' ? 'translate(60 40)' : 'translate(20 16)';
  return (
    <>
      <rect width="200" height="120" fill={DESK} />
      <g transform={`${pos} scale(${scale})${flaw === 'tilt' ? ' rotate(14 80 44)' : ''}`} filter={flaw === 'blur' ? 'url(#docsketch-blur)' : undefined}>
        <rect width="160" height="88" rx="7" fill={CARD} stroke={LINE} strokeWidth="1.5" />
        <CardBody kind={kind} />
      </g>
      {flaw === 'glare' ? <ellipse cx="108" cy="58" rx="46" ry="30" fill="#ffffff" opacity="0.92" /> : null}
      {flaw === 'dark' ? <rect width="200" height="120" fill="#000" opacity="0.62" /> : null}
    </>
  );
};

const SelfieShot = ({ flaw }: { flaw: SketchFlaw }) => {
  const r = flaw === 'far' ? 14 : 34;
  const cx = flaw === 'cut' ? 170 : 100;
  return (
    <>
      <rect width="200" height="120" fill="#e6ebe7" />
      <g filter={flaw === 'blur' ? 'url(#docsketch-blur)' : undefined} transform={flaw === 'tilt' ? 'rotate(22 100 60)' : undefined}>
        <path d={`M${cx - r * 1.5} 122 Q${cx} ${58 + r * 0.8} ${cx + r * 1.5} 122`} fill="#7f8f88" />
        <Face cx={cx} cy={flaw === 'far' ? 70 : 54} r={r} />
      </g>
      {flaw === 'dark' ? <rect width="200" height="120" fill="#000" opacity="0.62" /> : null}
    </>
  );
};

/** One reference picture: the right way, or one common mistake. */
export function DocSketch({ kind, flaw = 'good', title }: { kind: SketchKind; flaw?: SketchFlaw; title: string }) {
  return (
    <svg viewBox="0 0 200 120" role="img" aria-label={title} style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 10 }}>
      <title>{title}</title>
      <defs><filter id="docsketch-blur"><feGaussianBlur stdDeviation="3" /></filter></defs>
      {kind === 'selfie' ? <SelfieShot flaw={flaw} /> : <CardShot kind={kind} flaw={flaw} />}
    </svg>
  );
}

export const DOC_GUIDES: Record<SketchKind, { good: string; tips: string[]; mistakes: Array<{ flaw: SketchFlaw; label: string }> }> = {
  selfie: {
    good: 'Face in the middle, looking at the camera',
    tips: ['Stand in good light, facing a window', 'Remove sunglasses, cap and mask', 'Only your face in the photo, with a plain wall behind you'],
    mistakes: [{ flaw: 'far', label: 'Too far' }, { flaw: 'dark', label: 'Too dark' }, { flaw: 'cut', label: 'Face cut off' }],
  },
  pan: {
    good: 'Whole card, flat and straight',
    tips: ['Put the card flat on a plain table', 'All four corners must be visible', 'Name and PAN number must be easy to read'],
    mistakes: [{ flaw: 'cut', label: 'Corners cut off' }, { flaw: 'glare', label: 'Light shining on it' }, { flaw: 'blur', label: 'Blurry' }],
  },
  aadhaarFront: {
    good: 'Side with your photo, whole card',
    tips: ['Use the side with your photo and birth date', 'All four corners must be visible', 'The 12-digit number must be easy to read'],
    mistakes: [{ flaw: 'cut', label: 'Corners cut off' }, { flaw: 'glare', label: 'Light shining on it' }, { flaw: 'tilt', label: 'Tilted' }],
  },
  aadhaarBack: {
    good: 'Side with your address, whole card',
    tips: ['Use the side with your address', 'All four corners must be visible', 'The address must be easy to read'],
    mistakes: [{ flaw: 'cut', label: 'Corners cut off' }, { flaw: 'blur', label: 'Blurry' }, { flaw: 'dark', label: 'Too dark' }],
  },
};
